// utils/exportOccurrencesToExcel.ts
import * as XLSX from "xlsx";
import { Occurrence } from "../models/occurrence";
import { getOccurrenceStepLabelByStepValue } from "../enums/occurrenceStep";

export type ExportOccStage = "writing";

type AnyRec = Record<string, any>;

// ---- Helpers de extração/normalização ----
const pick = <T,>(...vals: T[]) =>
  vals.find((v) => v !== undefined && v !== null && v !== "");

const asStr = (v: unknown) =>
  v === undefined || v === null ? undefined : String(v);

const getProtocolNumber = (occ: AnyRec): string | undefined =>
  asStr(
    pick(
      occ.protocolNumber,
      occ.protocol_number,
      occ.protocol,
      occ?.occurrence?.protocolNumber,
      occ?.occurrence?.protocol,
      occ?.metadata?.protocolNumber
    )
  );

const getScheduleNumber = (occ: AnyRec): string | undefined =>
  asStr(pick(occ.scheduleNumber, occ?.schedule?.number, occ?.occurrence?.scheduleNumber));

const getCallNumber = (occ: AnyRec): string =>
  getProtocolNumber(occ) ?? getScheduleNumber(occ) ?? "-";

// ---- Conversão de datas ----
const toDateSafe = (d?: string | Date | null): Date | null => {
  if (!d) return null;
  if (d instanceof Date) return d;
  // tenta parsear; se vier sem timezone, o Date usará local time
  const parsed = new Date(d);
  return isNaN(parsed.getTime()) ? null : parsed;
};

export async function exportOccurrencesToExcel({
  occurrenceList,
  occurrenceStepAvailable, // mantido para compatibilidade com chamadas existentes
  filename = `kanban_${new Date().toISOString().slice(0, 10)}.xlsx`,
  onProgress,
}: {
  occurrenceList: Occurrence[];
  occurrenceStepAvailable: string[];
  filename?: string;
  onProgress?: (stage: ExportOccStage) => void;
}): Promise<void> {
  // Uma linha por etapa (INÍCIO e CONCLUSÃO)
  const rows: Record<string, any>[] = [];

  occurrenceList.forEach((occ: AnyRec) => {
    console.log('@@@ Exportando ocorrência:', occ);
    // Alguns lugares usam 'steps', outros 'occurrenceSteps'
    const allSteps: AnyRec[] = occ.steps ?? occ.occurrenceSteps ?? [];

    // Agrupar por tipo de etapa (id numérico ou string)
    const stepsByType: Record<
      string,
      { dtStarts: Date[]; dtEnds: Date[]; label: string }
    > = {};

    allSteps.forEach((step) => {
      const rawKey = step?.step ?? step?.stepId ?? "";
      const stepKey = String(rawKey);
      if (!stepsByType[stepKey]) {
        stepsByType[stepKey] = {
          dtStarts: [],
          dtEnds: [],
          label: getOccurrenceStepLabelByStepValue(stepKey) ?? stepKey ?? "-",
        };
      }
      const s = toDateSafe(step?.dtStart);
      const e = toDateSafe(step?.dtEnd);
      if (s) stepsByType[stepKey].dtStarts.push(s);
      if (e) stepsByType[stepKey].dtEnds.push(e);
    });

    Object.entries(stepsByType).forEach(([_, { dtStarts, dtEnds, label }]) => {
      const minDtStart =
        dtStarts.length > 0
          ? new Date(Math.min(...dtStarts.map((d) => d.getTime())))
          : null;

      const maxDtEnd =
        dtEnds.length > 0
          ? new Date(Math.max(...dtEnds.map((d) => d.getTime())))
          : null;

      const baseCols = {
        "Id do chamado (número da ocorrência / agendamento)": getProtocolNumber(occ) ?? getScheduleNumber(occ) ?? "-",
        "Chassi": occ.vehicleChassis ?? "-",
      };

      // INÍCIO
      rows.push({
        ...baseCols,
        "Data e hora": minDtStart ? minDtStart.toLocaleString("pt-BR") : "-",
                    "Tipo (início/conclusão)": "INÍCIO",
                    "Etapa": label,
      });

      // CONCLUSÃO
      rows.push({
        ...baseCols,
        "Data e hora": maxDtEnd ? maxDtEnd.toLocaleString("pt-BR") : "-",
                    "Tipo (início/conclusão)": "CONCLUSÃO",
                    "Etapa": label,
      });
    });

    // Caso alguma ocorrência não tenha nenhum step, ainda registramos uma linha mínima (opcional)
    if (allSteps.length === 0) {
      rows.push({
        "Id do chamado (número da ocorrência / agendamento)": getProtocolNumber(occ) ?? getScheduleNumber(occ) ?? "-",
        "Chassi": occ.vehicleChassis ?? "-",
        "Data e hora": "-",
                    "Tipo (início/conclusão)": "-",
                    "Etapa": "-",
      });
    }
  });

  onProgress?.("writing");
  const ws = XLSX.utils.json_to_sheet(rows);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Ocorrências");
  XLSX.writeFile(wb, filename);
}
